<template>
  <div
    ref="container"
    class="rg-zoom-pic-container"
    :class="{
      resizing,
      appearing,
      ['disappearing-to-top-right']: disappearingToTopRight,
    }"
    :style="cssVars"
  >
    <div class="main" @mousedown="mouseDownDrag">
      <div class="besel-left"></div>
      <div class="besel-right"></div>
      <div class="header">
        <span class="close-btn" @click="onClickCloseBtn"></span>
        <div class="track-content">
          <div class="track-ic">
            <img :src="riskLevelStatusObject.trackIcon" />
          </div>
          <div
            class="track-number aisea-text-12-400-white"
            :style="riskLevelStatusObject.trackNumber"
          >
            {{ trackNumber }}
          </div>
        </div>
      </div>
      <div class="body">
        <img :src="src" @load="load" v-show="!error" />
        <div v-show="error" class="rg-zoom-pic-error h-full">
          <span class="rg-zoom-pic-error-text aisea-text-10-400-white">{{
            errorMsg
          }}</span>
        </div>
      </div>
      <div class="footer">
        <div class="ic">
          <img src="@/assets/images/radarGrid/video_fill.svg" />
        </div>
        <div class="name">
          <span class="aisea-text-12-400-white">{{ aiseaCameraName }}</span>
        </div>
        <div class="reload">
          <div
            class="reload-btn aisea-text-12-400-white"
            :class="{
              ['is-reloadable']: hasImgUrlNew,
            }"
            @click="onClickReloadBtn()"
          >
            <span>{{ hasImgUrlNew ? "更新" : "最新" }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="resizers"
      :class="{
        ['show-for-dev']: resizer.showForDev,
        shrinkable: resizer.shrinkable,
      }"
    >
      <div
        v-for="({ className }, key) in resizer.parts"
        :key="key"
        :class="`${className}`"
        @mousedown="(event) => mouseDownResize(event, key)"
      ></div>
    </div>
  </div>
</template>

<script>
import { RadarGridMixin } from "@/mixins";
import Vue from "vue";

const sizeConstants = (() => {
  const imgInitCoef = 32;
  const imgInit = {
    width: 16 * imgInitCoef,
    height: 9 * imgInitCoef,
  };
  const besel = {
    left: {
      width: 14,
    },
    right: {
      width: 14,
    },
    top: {
      height: 28,
    },
    bottom: {
      height: 38,
    },
  };

  const wholeInit = {
    width: besel.left.width + imgInit.width + besel.right.width,
    height: besel.top.height + imgInit.height + besel.bottom.height,
  };

  return {
    besel,
    wholeInit,
  };
})();

const CalcBasedOnAspectRatio = ({ width, height }) => {
  const greatestCommonDivisor = (x, y) => {
    if (y === 0) return x;
    return greatestCommonDivisor(y, x % y);
  };

  const gcd = greatestCommonDivisor(width, height);
  const aspectRatio = {
    width: width / gcd,
    height: height / gcd,
  };

  return {
    heightByWidth: (width) => (width / aspectRatio.width) * aspectRatio.height,
    widthByHeight: (height) =>
      (height / aspectRatio.height) * aspectRatio.width,
  };
};

const calcBasedOnAspectRatio = CalcBasedOnAspectRatio(sizeConstants.wholeInit);

const [px, ms] = ((...suffixes) =>
  suffixes.map((suffix) => (value) => value + suffix))("px", "ms");

export default {
  name: "RgZoomPic",
  mixins: [RadarGridMixin],
  props: {
    invokerPosition: {
      x: Number,
      y: Number,
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMsg: "表示に失敗しました",
      position: {
        x: 0,
        y: 0,
      },
      trackNumber: "",
      riskLevel: 0,
      aiseaCameraName: "",
      src: "",
      hasImgUrlNew: false,
      move: {
        offsets: {
          position: {
            x: 0,
            y: 0,
          },
        },
      },
      sizes: {
        width: sizeConstants.wholeInit.width,
        height: sizeConstants.wholeInit.height,
      },
      appearing: false,
      disappearingToTopRight: false,
      disappearingToCenter: false,
      durations: {
        disappearingToTopRight: 200,
      },
      resizing: false,
      resizer: {
        showForDev: false,
        parts: {
          topLeft: {
            className: "top-left",
            cursor: {
              default: "nw-resize",
              shrinkable: "nwse-resize",
            },
          },
          top: {
            className: "top",
            cursor: {
              default: "n-resize",
              shrinkable: "ns-resize",
            },
          },
          topRight: {
            className: "top-right",
            cursor: {
              default: "ne-resize",
              shrinkable: "nesw-resize",
            },
          },
          right: {
            className: "right",
            cursor: {
              default: "e-resize",
              shrinkable: "ew-resize",
            },
          },
          bottomRight: {
            className: "bottom-right",
            cursor: {
              default: "se-resize",
              shrinkable: "nwse-resize",
            },
          },
          bottom: {
            className: "bottom",
            cursor: {
              default: "s-resize",
              shrinkable: "ns-resize",
            },
          },
          bottomLeft: {
            className: "bottom-left",
            cursor: {
              default: "sw-resize",
              shrinkable: "nesw-resize",
            },
          },
          left: {
            className: "left",
            cursor: {
              default: "w-resize",
              shrinkable: "ew-resize",
            },
          },
        },
        activePart: {},
        activePartResizeStaticArgs: {},
        shrinkable: false,
        offsets: {
          position: {
            x: 0,
            y: 0,
          },
          sizes: {
            width: 0,
            height: 0,
          },
        },
      },
    };
  },

  watch: {},

  computed: {
    cssVars() {
      return {
        "--left": px(this.position.x),
        "--top": px(this.position.y),
        "--width": px(this.sizes.width),
        "--height": px(this.sizes.height),
        "--besel-left-width": px(sizeConstants.besel.left.width),
        "--besel-right-width": px(sizeConstants.besel.right.width),
        "--besel-top-height": px(sizeConstants.besel.top.height),
        "--besel-bottom-height": px(sizeConstants.besel.bottom.height),
        "--duration-disappear-to-top-right": ms(
          this.durations.disappearingToTopRight,
        ),
      };
    },
    riskLevelStatusObject() {
      return this.createRiskLevelStatusLabel(this.riskLevel);
    },
  },

  methods: {
    setPopupReactiveValues({ trackNumber, riskLevel, aiseaCameraName }) {
      this.trackNumber = trackNumber;
      this.riskLevel = riskLevel;
      this.aiseaCameraName = aiseaCameraName;
    },
    async setImgUrl(imgUrl) {
      if (!imgUrl) {
        this.error = true;
        this.loading = false;
        return;
      }
      try {
        this.loading = true;
        this.src = imgUrl;
        this.error = false;
      } catch (error) {
        Vue.$log.debug(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    setHasImgUrlNew(hasImgUrlNew) {
      this.hasImgUrlNew = hasImgUrlNew;
    },

    load() {
      this.loading = false;
    },

    forceCursorToDocument(cursor) {
      const { style } = document.documentElement;
      style.setProperty("cursor", cursor, "important");
      style.setProperty("pointer-events", "none", "important");
    },

    forceCursorToDocumentByPart(shrinkable) {
      const { cursor: resizerPartCursor } = this.resizer.activePart;
      const cursor = shrinkable
        ? resizerPartCursor.shrinkable
        : resizerPartCursor.default;
      this.forceCursorToDocument(cursor);
    },

    // NOTE: getComputedStyle()の使用例として残しておくが、計算によらない行き当たりばったりな実装を呼び込みやすいので非推奨
    // forceCursorToDocumentByElement(element) {
    //   this.forceCursorToDocument(window.getComputedStyle(element).cursor);
    // },

    mouseDownResize(event, partKey) {
      this.resizing = true;
      this.resizer.activePart = this.resizer.parts[partKey];
      const activePartResizeStaticArgs =
        this.partKeyToMouseMoveResizeStaticArgs(partKey);
      this.resizer.activePartResizeStaticArgs = activePartResizeStaticArgs;
      document.addEventListener("mousemove", this.mouseMoveResize);
      document.addEventListener("mouseup", this.mouseUpResize);
      this.forceCursorToDocumentByPart(this.resizer.shrinkable);

      this.resizer.offsets.sizes.width = this.sizes.width;
      this.resizer.offsets.sizes.height = this.sizes.height;
      this.resizer.offsets.position.x = event.clientX;
      this.resizer.offsets.position.y = event.clientY;
    },

    mouseMoveResizeCore(
      event,
      { horizontal, positionAdding, positionFixer = () => {} } = {
        horizontal: false,
        positionAdding: false,
        positionFixer: ({ x, y }) => {
          this.position.x += x;
          this.position.y += y;
        },
      },
    ) {
      const { width: offsetWidth, height: offsetHeight } =
        this.resizer.offsets.sizes;
      const { x: offsetX, y: offsetY } = this.resizer.offsets.position;
      const { clientX, clientY } = event;
      const { width: initWidth, height: initHeight } = sizeConstants.wholeInit;
      const offsetSize = horizontal ? offsetWidth : offsetHeight;
      const movedSize = horizontal ? clientX - offsetX : clientY - offsetY;
      const nextSizeCalced = offsetSize + movedSize * (positionAdding ? 1 : -1);
      const minLimit = horizontal ? initWidth : initHeight;

      const shrinkable = nextSizeCalced >= minLimit;
      const nextSizeFixed = shrinkable ? nextSizeCalced : minLimit;
      this.resizer.shrinkable = shrinkable;
      this.forceCursorToDocumentByPart(shrinkable);

      const { heightByWidth, widthByHeight } = calcBasedOnAspectRatio;
      const calcAnother = horizontal ? heightByWidth : widthByHeight;
      const nextSizeAnother = calcAnother(nextSizeFixed);
      const nextWidth = horizontal ? nextSizeFixed : nextSizeAnother;
      const nextHeight = horizontal ? nextSizeAnother : nextSizeFixed;
      const fixingX = nextWidth - this.sizes.width;
      const fixingY = nextHeight - this.sizes.height;
      this.sizes.width = nextWidth;
      this.sizes.height = nextHeight;
      positionFixer({ x: fixingX, y: fixingY });
    },

    // NOTE: ほんとはdata関数戻り値として宣言するのが適切ではあるものの、関数を含むので抽象アルゴリズムと近い視野に置いた
    partKeyToMouseMoveResizeStaticArgs(partKey) {
      return {
        topLeft: {
          horizontal: true,
          positionAdding: false,
          positionFixer: ({ x, y }) => {
            this.position.x -= x;
            this.position.y -= y;
          },
        },
        top: {
          horizontal: false,
          positionAdding: false,
          positionFixer: ({ x, y }) => {
            this.position.x -= x / 2;
            this.position.y -= y;
          },
        },
        topRight: {
          horizontal: true,
          positionAdding: true,
          positionFixer: ({ y }) => {
            this.position.y -= y;
          },
        },
        right: {
          horizontal: true,
          positionAdding: true,
          positionFixer: ({ y }) => {
            this.position.y -= y / 2;
          },
        },
        bottomRight: {
          horizontal: true,
          positionAdding: true,
        },
        bottom: {
          horizontal: false,
          positionAdding: true,
          positionFixer: ({ x }) => {
            this.position.x -= x / 2;
          },
        },
        bottomLeft: {
          horizontal: true,
          positionAdding: false,
          positionFixer: ({ x }) => {
            this.position.x -= x;
          },
        },
        left: {
          horizontal: true,
          positionAdding: false,
          positionFixer: ({ x, y }) => {
            this.position.x -= x;
            this.position.y -= y / 2;
          },
        },
      }[partKey];
    },

    mouseMoveResize(event) {
      this.mouseMoveResizeCore(event, this.resizer.activePartResizeStaticArgs);
    },

    mouseUpResize() {
      this.resizing = false;
      document.documentElement.style.cursor = "";
      document.documentElement.style.pointerEvents = "";
      document.removeEventListener("mousemove", this.mouseMoveResize);
      document.removeEventListener("mouseup", this.mouseUpResize);
    },

    mouseDownDrag(event) {
      document.addEventListener("mousemove", this.mouseMoveDrag);
      document.addEventListener("mouseup", this.mouseUpDrag);

      const { offsetLeft, offsetTop } = this.$refs.container;
      this.move.offsets.position.x = event.clientX - offsetLeft;
      this.move.offsets.position.y = event.clientY - offsetTop;
    },

    mouseMoveDrag(event) {
      const next = {
        x: event.clientX - this.move.offsets.position.x,
        y: event.clientY - this.move.offsets.position.y,
      };
      this.position.x = next.x;
      this.position.y = next.y;
    },

    mouseUpDrag() {
      document.removeEventListener("mousemove", this.mouseMoveDrag);
      document.removeEventListener("mouseup", this.mouseUpDrag);
    },

    onClickReloadBtn() {
      if (!this.hasImgUrlNew) return;
      this.$emit("onClickReloadBtn");
    },

    onClickCloseBtn() {
      this.disappearingToTopRight = true;
      setTimeout(() => {
        this.$emit("onClickCloseBtn");
      }, this.durations.disappearingToTopRight);
    },

    setSpawningPosition() {
      const ref = this.$refs.container;
      const { clientWidth: thisClientWidth, clientHeight: thisClientHeight } =
        ref;
      const { clientWidth: areaClientWidth } =
        ref.parentNode.parentNode.parentNode;
      const tryingRect = ((
        generating = {
          left: this.invokerPosition.x - thisClientWidth / 2 + 12,
          top: this.invokerPosition.y - thisClientHeight - 12,
        },
      ) => {
        generating.right = generating.left + thisClientWidth;
        generating.bottom = generating.top + thisClientHeight;
        return generating;
      })();

      this.position.x = (() => {
        if (tryingRect.right > areaClientWidth - thisClientWidth / 2)
          return areaClientWidth - thisClientWidth - thisClientWidth / 2;
        return tryingRect.left;
      })();

      this.position.y = (() => {
        if (tryingRect.top < 0) return 0 + 12;
        return tryingRect.top;
      })();
    },
  },

  async mounted() {
    await this.$nextTick();
    this.setSpawningPosition();
    this.appearing = true;
  },

  created() {
    this.setImgUrl();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

@mixin flex-centering() {
  display: flex;
  justify-content: center;
  align-items: center;
}

$z-index-max: 2147483647;

.rg-zoom-pic-container {
  position: absolute;
  // margin-right: -50%;
  // transform: translate(-50%, -50%);
  z-index: 12;
  &.resizing {
    // 階層コントロールがないのであんまり意味ないけど・・・
    z-index: $z-index-max;
  }

  top: var(--top);
  left: var(--left);

  // align-items: center;
  width: var(--width);
  height: var(--height);

  background: #021431;
  border-radius: 5px;
  user-select: none;

  &,
  & .body {
    transform: scale(0);
    opacity: 0;
  }

  &.appearing {
    transform-origin: center center;
    animation: main-appearing 0.3s ease-out forwards;
    @keyframes main-appearing {
      from {
        transform: scale(0);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    .body {
      animation: body-appearing 0.3s ease-out forwards 0.3s;
      @keyframes body-appearing {
        0% {
          transform: scale(0);
          opacity: 0;
        }
        5% {
          transform: scale(0, 0.01);
          opacity: 0;
        }
        30% {
          transform: scale(1, 0.01);
          opacity: 1;
        }
        80% {
          transform: scale(1, 0.01);
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
    }
  }

  &.disappearing-to-top-right {
    transform-origin: top right;
    animation: disappearing-to-top-right var(--duration-disappear-to-top-right)
      ease-out forwards;
    @keyframes disappearing-to-top-right {
      from {
        transform: scale(1);
        opacity: 1;
      }
      to {
        transform: scale(0);
        opacity: 0;
      }
    }
  }

  .main {
    cursor: move;
    display: grid;
    grid-template-rows: var(--besel-top-height) 1fr var(--besel-bottom-height);
    grid-template-columns: var(--besel-left-width) 1fr var(--besel-right-width);
    width: 100%;
    height: 100%;
    position: absolute;

    .besel-left {
      grid-column: 1/2;
      grid-row: 1/4;
    }
    .besel-right {
      grid-column: 3/4;
      grid-row: 1/4;
    }

    .header {
      grid-column: 2/3;
      grid-row: 1/2;
      display: flex;
      align-items: center;
      grid-template-columns: 1fr auto;
      position: relative;

      .track-content {
        margin: 1px 0 0 -3px; // デザインに合わせて微調整
        display: grid;
        grid-template-columns: 24px 1fr;

        .track-ic {
          align-self: center;
          justify-self: center;
          pointer-events: none;

          img {
            display: block;
            margin: auto;

            path {
              color: #f00;
              fill: currentColor;
            }
          }
        }
      }

      .close-btn {
        display: block;
        position: absolute;
        bottom: 2px;
        right: 0;
        text-align: right;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #222222;
        cursor: pointer;
        user-select: none;

        &:hover {
          background-color: #333333;
          transition: background-color 0.3s ease;
          cursor: pointer;
        }

        &::before,
        &::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 2px;
          height: 12px;
          background: #fff;
        }

        &::before {
          transform: translate(-50%, -50%) rotate(45deg);
        }

        &::after {
          transform: translate(-50%, -50%) rotate(-45deg);
        }
      }
    }

    .body {
      grid-column: 2/3;
      grid-row: 2/3;
      background-color: #021431;
      border-radius: 5px;
      overflow: hidden; // ローカルだとアスヒが4:3の画像が配信されてくるので、やむなく(本来は不要)

      display: flex;
      align-items: center;

      img {
        vertical-align: top; // 超重要(余白消し)
        border-radius: 5px;
        user-drag: none;
        -webkit-user-drag: none;
        -moz-user-select: none;

        object-fit: contain; // 画像のアスペクト比を保持して表示
        max-width: 100%;
        max-height: 100%;
        flex-grow: 1;
      }

      /** 画像がないメッセージを表示する */
      .rg-zoom-pic-error {
        background-color: #222222;
        border-radius: 5px;
        width: 100%;
        @include flex-centering;
        .rg-zoom-pic-error-text {
          color: #fff;
        }
      }
    }

    .footer {
      grid-column: 2/3;
      grid-row: 3/4;
      display: grid;
      align-items: center;
      grid-template-rows: auto;
      grid-template-columns: 18px 1fr auto;

      $margin-adjusted: 0 0 0 8px; // デザインに合わせて微調整

      .ic {
        margin-top: 2px;
        margin: $margin-adjusted;
      }

      .name {
        margin: $margin-adjusted;
      }

      .reload {
        $background-inactive: #6c7178;
        $background-active: #2d9cdb;
        .reload-btn {
          display: inline;
          border-radius: 3px;
          background-color: $background-inactive;
          padding: 2px 5px;

          &.is-reloadable {
            background-color: $background-active;
            animation: activate 1.8s linear infinite;
            transform-origin: center;
            @keyframes activate {
              0% {
                background-color: $background-active;
              }
              20% {
                background-color: #ddd;
              }
              80% {
                background-color: $background-active;
              }
              100% {
                background-color: #333;
              }
            }
            &:hover {
              cursor: pointer;
            }
          }
        }
      }
    }
  }

  // grid-columnとgrid-rowのmapへの定義はそのままだと分数リテラルと解釈して割り算されてしまうため
  // (最終的にCSSでgrid子要素の定義だと解釈されるまでに計算が先行するんだろう)、
  // 文字列として定義してmap-get関数でリテラル出力する
  $parts: (
    top-left: (
      grid-column: "1/3",
      grid-row: "1/3",
      cursor: (
        default: nw-resize,
        shrinkable: nwse-resize,
      ),
    ),
    top: (
      grid-column: "3/4",
      grid-row: "1/2",
      cursor: (
        default: n-resize,
        shrinkable: ns-resize,
      ),
    ),
    top-right: (
      grid-column: "4/6",
      grid-row: "1/3",
      cursor: (
        default: ne-resize,
        shrinkable: nesw-resize,
      ),
    ),
    right: (
      grid-column: "5/6",
      grid-row: "3/4",
      cursor: (
        default: e-resize,
        shrinkable: ew-resize,
      ),
    ),
    bottom-right: (
      grid-column: "4/6",
      grid-row: "4/6",
      cursor: (
        default: se-resize,
        shrinkable: nwse-resize,
      ),
    ),
    bottom: (
      grid-column: "3/4",
      grid-row: "5/6",
      cursor: (
        default: s-resize,
        shrinkable: ns-resize,
      ),
    ),
    bottom-left: (
      grid-column: "1/3",
      grid-row: "4/6",
      cursor: (
        default: sw-resize,
        shrinkable: nesw-resize,
      ),
    ),
    left: (
      grid-column: "1/2",
      grid-row: "3/4",
      cursor: (
        default: w-resize,
        shrinkable: ew-resize,
      ),
    ),
  );
  .resizers {
    $border-width: 5px;
    $corner-width: 6px;
    $subtract-from-full: ($border-width + $corner-width) * 2;
    position: absolute;
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns:
      $border-width $corner-width calc(100% - #{$subtract-from-full})
      $corner-width $border-width;
    grid-template-rows:
      $border-width $corner-width calc(100% - #{$subtract-from-full})
      $corner-width $border-width;
    pointer-events: none;
    & > * {
      pointer-events: auto;
      background: transparent;
    }

    // 子要素1つごとに全階層分の定義を展開するのではなく、階層定義の見通しを優先して「あるべき階層で」毎回展開する
    @each $key, $part in $parts {
      .#{$key} {
        grid-column: #{map-get($part, grid-column)};
        grid-row: #{map-get($part, grid-row)};
      }
    }

    &.show-for-dev {
      & > * {
        opacity: 0.5;
        background: yellow;
      }
    }
  }
  &.resizing {
    pointer-events: none;
  }
  &:not(.resizing) {
    .resizers {
      // 子要素1つごとに全階層分の定義を展開するのではなく、階層定義の見通しを優先して「あるべき階層で」毎回展開する
      @each $key, $part in $parts {
        $cursor: map-get($part, cursor);
        .#{$key} {
          cursor: map-get($cursor, default);
        }
      }
      &.shrinkable {
        // 子要素1つごとに全階層分の定義を展開するのではなく、階層定義の見通しを優先して「あるべき階層で」毎回展開する
        @each $key, $part in $parts {
          $cursor: map-get($part, cursor);
          .#{$key} {
            cursor: map-get($cursor, shrinkable);
          }
        }
      }
    }
  }
}
</style>
